"use client"
import { useEffect, useRef, useState } from "react";
import { withMediaUrl } from "@/app/lib/basePath";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/ui/accordion";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, Clock, Facebook, Linkedin, Mail, X, XIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import CTASection from "../home/CTA/CTASection";
import { getLanguageFromPathname, withLanguagePrefix } from "@/app/lib/i18n-routing";
import { usePathname } from "next/navigation";
import AIProviders from "../ui/AIProviders";
import { defaultAIProvidersConfig } from "@/app/lib/config/aiProviders";
import ButtonGroup from "../ui/ButtonGroup";


export default function BlogPageDetail(data: any) {
    const pathname = usePathname();
    const currentLanguage = getLanguageFromPathname(pathname);
    const faqs = data.data.content.filter((item: any) => item.type === "faq");
    const posts = [
        { "slug": "digital-transformation-real-estate", "title": "Digital Transformation in Real Estate", "excerpt": "How technology is reshaping the real estate industry and what builders need to know.", "date": "2024-03-15", "category": "Industry" },
        { "slug": "benefits-interactive-site-maps", "title": "Benefits of Interactive Site Maps", "excerpt": "Why interactive digital maps are becoming essential for modern real estate developers.", "date": "2024-03-10", "category": "Product" },
        { "slug": "streamlining-plot-booking", "title": "Streamlining the Plot Booking Process", "excerpt": "Best practices for creating a seamless digital booking experience for property buyers.", "date": "2024-03-05", "category": "Tips" }
    ]
    const post = posts[0];
    const postData = data.data;
    const videoRef = useRef<HTMLVideoElement>(null);
    const mediaType = typeof postData?.mediaType === "string"
        ? postData.mediaType.trim().toLowerCase()
        : "";
    const mediaValue = postData?.media ?? postData?.coverImage;
    const mediaAssets = (Array.isArray(mediaValue) ? mediaValue : [mediaValue])
        .filter((media: any) => Boolean(media?.url));
    const [mediaIndex, setMediaIndex] = useState(0);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const activeMedia = mediaAssets[mediaIndex] ?? mediaAssets[0];
    const mediaUrl = withMediaUrl(activeMedia?.url);
    const thumbnailValue = postData?.videoThumbnail;
    const thumbnail = Array.isArray(thumbnailValue) ? thumbnailValue[0] : thumbnailValue;
    const thumbnailUrl = withMediaUrl(thumbnail?.url);
    const embedUrl = postData?.embedUrl;
    const isImage = mediaType === "image" || (!mediaType && Boolean(mediaUrl));
    const isVideo = mediaType === "video";
    const isEmbed = mediaType.includes("embed");
    const hasBannerMedia = Boolean(mediaUrl || embedUrl);
    const bannerButtons = Array.isArray(postData?.button ?? postData?.buttons)
        ? postData.button ?? postData.buttons
        : postData?.button ?? postData?.buttons
            ? [postData.button ?? postData.buttons]
            : [];
    const title = postData.title ?? "";
    const highlight = postData.titleHighlight?.trim();
    const highlightIndex = highlight
        ? title.toLowerCase().indexOf(highlight.toLowerCase())
        : -1;
    const [shareUrl, setShareUrl] = useState("");

    useEffect(() => {
        setShareUrl(window.location.href);
    }, []);

    useEffect(() => {
        setMediaIndex(0);
        setIsVideoReady(false);
        setShouldLoadVideo(false);
    }, [postData?.id, postData?.slug]);

    useEffect(() => {
        if (!isImage || mediaAssets.length < 2) return;
        const timer = window.setInterval(
            () => setMediaIndex((index) => (index + 1) % mediaAssets.length),
            5000,
        );
        return () => window.clearInterval(timer);
    }, [isImage, mediaAssets.length]);

    useEffect(() => {
        const video = videoRef.current;
        if (!isVideo || !mediaUrl || !video) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoadVideo(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(video);
        return () => observer.disconnect();
    }, [isVideo, mediaUrl]);

    useEffect(() => {
        if (!shouldLoadVideo) return;
        void videoRef.current?.play().catch(() => undefined);
    }, [shouldLoadVideo, mediaUrl]);

    const shareTitle = postData.title || "";
    const shareText = encodeURIComponent(shareTitle);
    const shareBody = encodeURIComponent(`${postData.excerpt || ""}\n\n${shareUrl}`);
    const socialShareLinks = [
        {
            name: "Facebook",
            href: shareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` : "#",
            icon: Facebook,
            label: "Share on Facebook",
        },
        {
            name: "LinkedIn",
            href: shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` : "#",
            icon: Linkedin,
            label: "Share on LinkedIn",
        },
        {
            name: "X",
            href: shareUrl ? `https://x.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}` : "#",
            icon: X,
            label: "Share on X",
        },
        {
            name: "Mail",
            href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${shareBody}`,
            icon: Mail,
            label: "Share by email",
        },
    ];

    return (
        <>
            <section className="relative custom-minheight flex flex-col items-center justify-center">
                <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[1000px] pointer-events-none top-0 w-full">
                    <div className='shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40'></div>
                    <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto relative z-10 pt-24 lg:pt-32 pb-4 md:pb-6 lg:pb-8">
                    <motion.div initial="hidden" animate="visible" variants={stagger}>
                        
                        <motion.h1
                            variants={fadeUp}
                            className="banner-title max-w-4xl mx-auto text-center"
                        >
                            {highlightIndex >= 0 && highlight ? (
                                <>
                                    {title.slice(0, highlightIndex)}
                                    <span className="banner-highlight-title">
                                        {title.slice(highlightIndex, highlightIndex + highlight.length)}
                                    </span>
                                    {title.slice(highlightIndex + highlight.length)}
                                </>
                            ) : (
                                title
                            )}
                        </motion.h1>
                        <div className="flex items-center gap-3 mt-3 lg:mt-8 justify-center flex-col sm:flex-row">
                            
                            <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm">{postData.category.name}</span>
                                <motion.div variants={fadeUp} className="flex items-center gap-2 banner-description !text-sm">
                                    <Calendar className="w-5 h-5" />
                                    {new Date(postData.publishAt).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </motion.div>
                                <motion.div variants={fadeUp} className="flex items-center gap-2 banner-description !text-sm">
                                    <Clock className="w-5 h-5" />
                                   {postData.minutesToRead}
                                </motion.div>
                        </div>

                    </motion.div>      
                    {hasBannerMedia && (
                        <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="mt-6 lg:mt-10 max-w-4xl mx-auto"
                        >
                            <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <motion.div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                        <Image
                                            src="https://naavik.ai/favicon.svg"
                                            alt="Author"
                                            className="w-full h-full object-cover"
                                            title="Author"
                                            width={48}
                                            height={48}
                                            loading="lazy"
                                            fetchPriority="high"
                                            unoptimized
                                        />
                                    </motion.div>
                                    <motion.div className="text-white text-lg font-medium leading-none">
                                        {postData.author.name}
                                    </motion.div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {socialShareLinks.map(({ name, href, icon: Icon, label }) => (
                                        <a
                                            key={name}
                                            href={href}
                                            target={href === "#" ? undefined : "_blank"}
                                            rel={href === "#" ? undefined : "noopener noreferrer"}
                                            aria-label={label}
                                            title={label}
                                            className="text-white/50 hover:text-white hover:scale-110 transition-all duration-300"
                                        >
                                            <Icon className="w-5 h-5" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        <div className="relative">
                        <div aria-hidden className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-3xl blur-2xl w-full md:w-auto" />
                        {isImage && mediaUrl && (
                            <motion.div className="relative bg-[#000c1e]/40 backdrop-blur-xl rounded-2xl flex items-center justify-center p-2 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] border border-white/20">
                                <Image key={mediaUrl} src={mediaUrl} alt={activeMedia?.alternativeText || "Blog image"} title={activeMedia?.alternativeText || "Blog image"} className="object-cover w-full h-full rounded-[10px]" width={512} height={378} decoding="async" loading="lazy" fetchPriority="high" />
                                {mediaAssets.length > 1 && (
                                    <>
                                        <button type="button" onClick={() => setMediaIndex((index) => (index - 1 + mediaAssets.length) % mediaAssets.length)} aria-label="Show previous blog image" className="absolute left-4 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/70 text-white shadow-lg transition hover:bg-black"><ChevronLeft className="h-6 w-6" /></button>
                                        <button type="button" onClick={() => setMediaIndex((index) => (index + 1) % mediaAssets.length)} aria-label="Show next blog image" className="absolute right-4 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/70 text-white shadow-lg transition hover:bg-black"><ChevronRight className="h-6 w-6" /></button>
                                    </>
                                )}
                            </motion.div>
                        )}
                        {isVideo && mediaUrl && (
                            <motion.div className="relative aspect-video bg-[#000c1e]/40 backdrop-blur-xl rounded-2xl flex items-center justify-center p-2 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] border border-white/20">
                                {thumbnailUrl && !isVideoReady && <Image src={thumbnailUrl} alt={thumbnail?.alternativeText || "Video thumbnail"} fill sizes="(max-width: 896px) 100vw, 896px" className="z-10 rounded-[10px] object-cover" />}
                                <video ref={videoRef} className="h-full w-full rounded-[10px] object-cover" autoPlay={shouldLoadVideo} loop muted playsInline preload={shouldLoadVideo ? "auto" : "none"} poster={thumbnailUrl || undefined} onCanPlay={() => void videoRef.current?.play().catch(() => undefined)} onPlaying={() => setIsVideoReady(true)}>{shouldLoadVideo && <source src={mediaUrl} type={activeMedia?.mime || "video/mp4"} />}</video>
                            </motion.div>
                        )}
                        {isEmbed && embedUrl && (
                            <motion.div className="aspect-[9/9] md:aspect-[16/8] overflow-hidden rounded-[14px] bg-black/20 p-3 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] backdrop-blur-[20px]"><iframe className="h-full w-full rounded-[10px] border-0" src={embedUrl} title={postData.title || "Live project"} /></motion.div>
                        )}
                        </div>
                        <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-3">
                                {isEmbed && bannerButtons.length > 0 && (
                                    <div className="w-full min-w-0 md:w-auto">
                                        <ButtonGroup buttons={bannerButtons} containerClass="flex flex-wrap items-center gap-2" btnClassPrimary="btn-primary !px-4 !py-2 !text-sm whitespace-nowrap" btnClassSecondary="btn-secondary !px-4 !py-2 !text-sm whitespace-nowrap" />
                                    </div>
                                )}
                                <div className="w-full min-w-0 overflow-hidden md:flex-1 md:flex md:justify-end">
                                    <AIProviders
                                    data={postData?.ai_configuration || defaultAIProvidersConfig}
                                    variant="inline"
                                    size="sm"
                                    showTitle={true}
                                    showLabelsOnMobile={false}
                                    className="w-full justify-start md:justify-end"
                                    providersClassName="flex-wrap gap-1 sm:gap-2"
                                    />
                                </div>
                        </div>
                    </motion.div>
                    )}                 
                </div>
            </section>

            <section className="py-4 md:py-6 lg:py-8">
                <div className="container mx-auto">
                    <div className="post-content mx-auto max-w-4xl ">


                        {/* <p>
                            Large language models (LLMs) are brilliant reasoners. But without a way
                            to interact with the world, they’re essentially locked behind a glass
                            wall.
                        </p>

                        <h2 id="what-llm-tool-calling-means">
                            What LLM tool calling means?
                        </h2>

                        <p>
                            LLM tool calling is a mechanism that allows an AI model to generate
                            structured requests — typically in JSON — to invoke external functions
                            or APIs.
                        </p>

                        <figure className="kg-card kg-image-card kg-card-hascaption">
                            <img
                                src="https://storage.ghost.io/c/0d/78/0d78b34c-0c5f-4975-900e-61d00ccb1c2d/content/images/2026/04/data-src-image-9e22bd18-7873-4e55-8134-834eb81e9afb.png"
                                className="kg-image"
                                alt="An AI agent uses tool calling to interact with external systems"
                            />

                            <figcaption>
                                <i>An AI agent uses tool calling to interact with external systems</i>
                            </figcaption>
                        </figure>

                        <h2 id="how-llm-tool-calling-works">
                            How LLM tool calling works?
                        </h2>

                        <ul>
                            <li>
                                <strong>Automatic tool invocation:</strong> The LLM decides
                                dynamically if and when to call a tool.
                            </li>

                            <li>
                                <strong>Forced tool invocation:</strong> The system developer
                                configures the model to always use a specific tool.
                            </li>
                        </ul>

                        <h3 id="fetch-context">Fetch context</h3>

                        <p>
                            The system prepares the environment by gathering the prompt, user
                            history, and the tool definitions.
                        </p>

                        <h3 id="select-a-tool">Select a tool</h3>

                        <p>
                            The user submits a prompt. The LLM analyzes the request against its
                            available tools.
                        </p>

                        <h3 id="validate-the-tool-call">Validate the tool call</h3>

                        <p>
                            The application layer validates that the generated JSON matches the
                            expected schema.
                        </p>

                        <h3 id="execute-the-external-call">
                            Execute the external call
                        </h3>

                        <p>
                            The system executes the function or API call.
                        </p>

                        <h3 id="feed-results-back-to-the-llm">
                            Feed results back to the LLM
                        </h3>

                        <p>
                            The output of the tool is sent back to the LLM in a new message.
                        </p>

                        <h3 id="generate-final-response">
                            Generate final response
                        </h3>

                        <p>
                            With the new data in its context window, the LLM performs a final
                            inference pass.
                        </p>

                        <h2 id="production-reliability-and-observability">
                            Production reliability and observability
                        </h2>

                        <p>
                            In production, a failed tool call can result in duplicate charges,
                            corrupted database records, or broken workflows.
                        </p>

                        <h3 id="authentication-and-credential-lifecycle">
                            Authentication and credential lifecycle
                        </h3>

                        <p>
                            A production-grade orchestration layer must act as a secure proxy.
                        </p>

                        <h3 id="heterogeneity-of-apis-and-schema-mapping">
                            Heterogeneity of APIs and schema mapping
                        </h3>

                        <p>
                            Every API has its own personality and response format.
                        </p>

                        <h3 id="error-handling-and-soft-failures">
                            Error handling and soft failures
                        </h3>

                        <ul>
                            <li>
                                <strong>System-level retry:</strong> The orchestration layer retries
                                the call.
                            </li>

                            <li>
                                <strong>Model-level reasoning:</strong> The error message is fed back
                                to the LLM.
                            </li>
                        </ul>

                        <h3 id="observability-and-tracing-the-agentic-loop">
                            Observability and tracing the agentic loop
                        </h3>

                        <p>
                            Observability helps developers inspect every execution step in the AI
                            workflow.
                        </p>

                        <h2 id="how-to-implement-tool-calling-in-your-ai-workflows">
                            How to implement tool calling in your AI workflows?
                        </h2>

                        <p>
                            The six-step tool calling process works across multiple LLM providers.
                        </p>

                        <h3 id="how-to-implement-tool-calling-in-n8n">
                            How to implement tool calling in n8n?
                        </h3>

                        <p>
                            In n8n, tool calling is built into the AI Agent node.
                        </p>

                        <figure className="kg-card kg-image-card kg-card-hascaption">
                            <img
                                src="https://storage.ghost.io/c/0d/78/0d78b34c-0c5f-4975-900e-61d00ccb1c2d/content/images/2026/04/data-src-image-317fcfe4-5f14-45f8-a2d3-2c4e06933114.png"
                                className="kg-image"
                                alt="Workflow example"
                            />

                            <figcaption>
                                AI workflow showing a chat-triggered agent using multiple tools.
                            </figcaption>
                        </figure>

                        <div className="kg-card kg-callout-card kg-callout-card-blue">
                            <div className="kg-callout-emoji">💡</div>

                            <div className="kg-callout-text">
                                <strong>
                                    Pro tip: Pack multiple API calls into a sub-workflow.
                                </strong>
                            </div>
                        </div>

                        <h2 id="the-future-of-ai-tooling-scaling-the-agentic-loop">
                            The future of AI tooling
                        </h2>

                        <p>
                            The focus is shifting from single-tool execution to multi-agent
                            orchestration.
                        </p>

                        <h2 id="beyond-the-prompt-building-resilient-agentic-systems">
                            Beyond the prompt
                        </h2>

                        <p>
                            LLM tool calling enables AI systems to move from passive reasoning to
                            real-world actions.
                        </p> */}

                        {postData?.content?.map((item: any, index: number) => (
                            <div key={`${item.type}-${index}`}>
                                {item.type === "text" && (
                                    <div
                                        className="prose prose-invert max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: item.html,
                                        }}
                                    />
                                )}
                                {item.type === "image" && (
                                    <figure className="space-y-3">

                                        <div className="relative  overflow-hidden rounded-2xl">
{/* aspect-video */}
                                            <Image
                                                src={withMediaUrl(item.imageUrl)}
                                                alt={item.imageAlt || "Blog Image"}
                                                title={item.imageAlt || "Blog Image"}
                                                className="object-cover"
                                                width="900"
                                                height="574"
                                                decoding="async"
                                                loading="lazy"
                                                fetchPriority="high"
                                            />

                                        </div>

                                        {item.caption && (
                                            <figcaption className="text-center text-sm text-white/60">
                                                {item.caption}
                                            </figcaption>
                                        )}

                                    </figure>
                                )}

                            </div>
                        ))}
                        {faqs.length > 0 && (
                            <>
                            <h2 className="section-title mb-1 lg:mb-4 text-center mt-12">
                                Frequently Asked Questions
                            </h2>
                            <section className="relative py-6 lg:py-8">
                                <div aria-hidden className="absolute inset-0 star-field pointer-events-none" />
                                <div className="container mx-auto max-w-4xl relative z-10">
                                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                                        <Accordion type="single" collapsible className="space-y-3">
                                            {faqs.map((faq: any, i: number) => (
                                                <motion.div key={i} variants={fadeUp}>
                                                    <AccordionItem
                                                        value={`item-${i}`}
                                                        className="group rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_4px_24px_-12px_rgba(0,0,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.06] hover:border-white/20 hover:shadow-[0_8px_32px_-12px_hsl(var(--primary)/0.25)] data-[state=open]:bg-white/[0.07] data-[state=open]:border-white/25 px-5"
                                                    >
                                                        <AccordionTrigger className="hover:no-underline text-left font-semibold py-3.5 leading-normal ">{faq.question}</AccordionTrigger>
                                                        <AccordionContent className="text-muted-foreground leading-relaxed pb-5 -translate-y-1">
                                                            <div
                                                                className="post-content mx-auto max-w-4xl "
                                                                dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
                                                            />
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                </motion.div>
                                            ))}
                                        </Accordion>
                                    </motion.div>
                                </div>
                            </section>
                            </>
                        )}


                    </div>
                </div>
            </section>

            {/* <section className="py-4 md:py-6 lg:py-8 relative z-[1]">
                <div className="container mx-auto">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <motion.div variants={fadeUp} className='flex flex-col'>
                            <Link href={`/blogs/${post.slug}`} className="flex flex-col group flex-grow">
                                <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10  rounded-lg flex-grow flex flex-col">
                                    
                                    <div className='aspect-video relative'>
                                        <img className='object-cover h-full w-full' src="https://plus.unsplash.com/premium_photo-1720744786849-a7412d24ffbf?q=80&w=809&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="" />
                                        <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">{post.category}</span>
                                    </div>
                                    <div className="overflow-hidden group p-6 flex flex-col flex-grow">
                                        <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">{post.title}</h3>
                                        <p className="mt-2 text-white/70 text-sm leading-[1.7]">How technology is reshaping the real estate industry and what builders need to know.</p>
                                        <div className='flex items-center gap-3 justify-between mt-auto'>
                                            <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                Read More <ArrowRight className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs text-white/70 flex items-center gap-1"><Calendar className="w-3 h-3" />2024-03-15</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                        <motion.div variants={fadeUp} className='flex flex-col'>
                            <Link href={`/blogs/${post.slug}`} className="flex flex-col group flex-grow">
                                <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10  rounded-lg flex-grow flex flex-col">
                                   
                                    <div className='aspect-video relative'>
                                        <img className='object-cover h-full w-full' src="https://plus.unsplash.com/premium_photo-1720744786849-a7412d24ffbf?q=80&w=809&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="" />
                                        <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">{post.category}</span>
                                    </div>
                                    <div className="overflow-hidden group p-6 flex flex-col flex-grow">
                                        <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">{post.title}</h3>
                                        <p className="mt-2 text-white/70 text-sm leading-[1.7]">How technology is reshaping the real estate industry and what builders need to know.</p>
                                        <div className='flex items-center gap-3 justify-between mt-auto'>
                                            <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                Read More <ArrowRight className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs text-white/70 flex items-center gap-1"><Calendar className="w-3 h-3" />2024-03-15</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                        <motion.div variants={fadeUp} className='flex flex-col'>
                            <Link href={`/blogs/${post.slug}`} className="flex flex-col group flex-grow">
                                <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10  rounded-lg flex-grow flex flex-col">
                                  
                                    <div className='aspect-video relative'>
                                        <img className='object-cover h-full w-full' src="https://plus.unsplash.com/premium_photo-1720744786849-a7412d24ffbf?q=80&w=809&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="" />
                                        <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">{post.category}</span>
                                    </div>
                                    <div className="overflow-hidden group p-6 flex flex-col flex-grow">
                                        <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">{post.title}</h3>
                                        <p className="mt-2 text-white/70 text-sm leading-[1.7]">How technology is reshaping the real estate industry and what builders need to know.</p>
                                        <div className='flex items-center gap-3 justify-between mt-auto'>
                                            <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                Read More <ArrowRight className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs text-white/70 flex items-center gap-1"><Calendar className="w-3 h-3" />2024-03-15</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section> */}
            { postData.recommendedBlogs && postData.recommendedBlogs.length > 0  && (
            <>
                {/* <div className="container mx-auto"> */}
                    <h2 className="section-title !mb-2 text-center">
                        Recommended Reads
                    </h2>
                {/* </div> */}

                <section className="py-6 lg:py-8 relative z-[1]">
                    <div className="container mx-auto">

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={stagger}
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-8 mx-auto max-w-6xl"
                        >

                            {postData.recommendedBlogs?.map((post: any) => (
                                <motion.div
                                    key={post.id}
                                    variants={fadeUp}
                                    className="flex flex-col"
                                >
                                    <Link
                                        href={withLanguagePrefix(`/blogs/${post.slug}`,currentLanguage)}
                                        className="flex flex-col group flex-grow"
                                        title={post.title}
                                    >

                                        <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10 rounded-lg flex-grow flex flex-col">

                                            <div className=" relative">

                                                <Image
                                                    className="object-cover h-full w-full"
                                                    src={withMediaUrl(post.coverImage?.url)}
                                                    alt={post.title}
                                                    title={post.title}
                                                    width={360}
                                                    height={202}
                                                    loading="lazy"
                                                />

                                                <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">
                                                    {post.category.name}
                                                </span>

                                            </div>

                                            <div className="overflow-hidden group p-6 flex flex-col flex-grow">

                                                <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">
                                                    {post.title}
                                                </h3>

                                                <p className="mt-2 text-white/70 text-sm leading-[1.7]">
                                                    {post.excerpt}
                                                </p>

                                                <div className="flex items-center gap-3 justify-between mt-auto">

                                                    <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                        Read More
                                                        <ArrowRight className="w-4 h-4" />
                                                    </div>

                                                    <span className="text-xs text-white/70 flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(post.publishAt).toLocaleDateString("en-GB", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}

                                                    </span>

                                                </div>

                                            </div>

                                        </div>

                                    </Link>
                                </motion.div>
                            ))}

                        </motion.div>

                    </div>
                </section>
                
                <CTASection data={postData.transform_section} />
            </>
            
            )}
        </>
    );
}
